import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import classNames from 'classnames';

import { useMediaQuery } from '@material-ui/core';
import { SingleDatePicker as DatePicker } from 'react-dates';

import { Typography, useTheme } from '@material-ui/core';
import FormHelperText from '@material-ui/core/FormHelperText';
import DateRangeIcon from '@material-ui/icons/DateRange';

import { LOCALES_OBJ } from 'constants/base';

import { useStyles } from './styles';
import { getIsHalf } from '../../utils/book-accommodation/getIsHalf';

const CalendarImage = ({ fill }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.625 1.8125V0.6875H12.5V1.8125H3.5V0.6875H2.375V1.8125H0.125V13.625C0.125 14.557 0.880519 15.3125 1.8125 15.3125H14.1875C15.1195 15.3125 15.875 14.557 15.875 13.625V1.8125H13.625ZM14.75 13.625C14.75 13.9357 14.4982 14.1875 14.1875 14.1875H1.8125C1.50184 14.1875 1.25 13.9357 1.25 13.625V2.9375H2.375V4.625H3.5V2.9375H12.5V4.625H13.625V2.9375H14.75V13.625ZM2.375 9.125H5.75V5.75H2.375V9.125ZM3.5 6.875H4.625V8H3.5V6.875ZM9.6875 9.125H6.3125V5.75H9.6875V9.125ZM8.5625 6.875H7.4375V8H8.5625V6.875ZM10.25 9.125H13.625V5.75H10.25V9.125ZM11.375 6.875H12.5V8H11.375V6.875ZM5.75 13.0625H2.375V9.6875H5.75V13.0625ZM4.625 10.8125H3.5V11.9375H4.625V10.8125ZM6.3125 13.0625H9.6875V9.6875H6.3125V13.0625ZM7.4375 10.8125H8.5625V11.9375H7.4375V10.8125Z"
      fill={fill || '#0E8BB4'}
    />
  </svg>
);

const SingleDatePicker = ({
  id,
  isStartDateRenderMonth,
  input,
  label,
  className,
  availableRange,
  disabledDates,
  changeMonthsCallback,
  onChangeCallback,
  closeCalendarCallback,
  isInput,
  numberOfMonths,
  labelTypographyVariant,
  cutoffData,
  daySize,
  labelClass,
  inputLabelClass,
  customInputClass,
  isWidgetSearch,
  placeholder,
  isFocused,
  dateRender,
  isIcon,
  meta: { touched, error, submitFailed }
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const [t, i18n] = useTranslation();

  const selectedLanguage = i18n.language;

  const [focus, setFocus] = React.useState(false);
  const [isHiddenNextButton, setHiddenNextButton] = React.useState(false);

  const [render, setRender] = React.useState(true);
  const [renderMonth, setRenderMonth] = React.useState(
    isStartDateRenderMonth && input.value ? input.value : moment()
  );

  React.useEffect(() => {
    if (typeof isFocused === 'boolean') {
      setFocus(isFocused);

      if (dateRender) {
        setRenderMonth(dateRender);
      }
    }
  }, [isFocused]);

  React.useEffect(() => {
    setRender(!render);
    setTimeout(() => {
      setRender(render);
    });
  }, [selectedLanguage]);



  const onChangeHandler = date => {
    input.onChange(date);
    if (onChangeCallback) onChangeCallback(date);
  };

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const onPrevMonthClickHandler = date => {
    setRenderMonth(date);
    setHiddenNextButton(false);
    if (changeMonthsCallback) changeMonthsCallback(date);
  };

  const onNextMonthClickHandler = date => {
    setRenderMonth(date);
    const currentDate = moment();
    const endAvailableDate = moment(currentDate).add(23, 'M');

    const isTheSameDates = moment(date)
      .startOf('month')
      .isSame(endAvailableDate.startOf('month'));

    if (isTheSameDates) setHiddenNextButton(true);
    if (changeMonthsCallback) changeMonthsCallback(date);
  };

  const isBlockDayByCutoffPeriodWithoutTime = (
    cutoffPeriod,
    cutoffPeriodTimeType,
    isDayBlocked,
    date
  ) => {
    if (!cutoffPeriod) return isDayBlocked;

    const dateWithTime = moment(date).set({
      hour: 0,
      minute: 0
    });
    const nowCutoffDate = moment().add(cutoffPeriod, cutoffPeriodTimeType);

    if (moment(dateWithTime).isSameOrBefore(nowCutoffDate, 'day')) {
      return true;
    }

    return false;
  };

  const isBlockDayByCutoffPeriodWithTime = (
    cutoffPeriod,
    cutoffPeriodTimeType,
    startTimeAndDuration,
    date
  ) => {
    const disabledStartTimeList = startTimeAndDuration.map(item => {
      const startTime = moment(item.startTime, 'HH:mm');
      const dateWithTime = moment(date).set({
        hour: startTime.get('hour'),
        minute: startTime.get('minute')
      });
      const nowCutoffDate = moment().add(cutoffPeriod, cutoffPeriodTimeType);
      const difference = moment(dateWithTime).diff(nowCutoffDate);
      return difference <= 0;
    });

    if (disabledStartTimeList.every(value => value)) {
      return true;
    }

    return false;
  };

  const isBlockDayByCutoffPeriod = (date, isDayBlocked) => {
    if (isDayBlocked) return isDayBlocked;

    if (cutoffData) {
      const days = cutoffData.days;
      const daysArray = cutoffData.daysArray;

      const cutoffPeriod = parseInt(cutoffData.cutoffPeriod);
      const cutoffPeriodTimeType = cutoffData.cutoffPeriodTimeType;

      if (days) {
        const isDisabled = isBlockDayByCutoffPeriodWithoutTime(
          cutoffPeriod,
          cutoffPeriodTimeType,
          isDayBlocked,
          date
        );
        if (isDisabled) return true;
      } else if (daysArray) {
        let isDisabledDate = false;

        daysArray.map(item => {
          const days = item.days;
          const startTimeAndDuration = item.startTimeAndDuration;

          const dateDay = moment(date).format('ddd');

          if (days[dateDay]) {
            if (startTimeAndDuration.length) {
              const isDisabled = isBlockDayByCutoffPeriodWithTime(
                cutoffPeriod,
                cutoffPeriodTimeType,
                startTimeAndDuration,
                date
              );
              if (isDisabled) isDisabledDate = true;
            } else {
              const isDisabled = isBlockDayByCutoffPeriodWithoutTime(
                cutoffPeriod,
                cutoffPeriodTimeType,
                isDayBlocked,
                date
              );
              if (isDisabled) isDisabledDate = true;
            }
          }
        });

        if (isDisabledDate) return true;
      }
    }

    return isDayBlocked;
  };

  const isBlockedDay = date => {
    if (
      disabledDates &&
      disabledDates.includes(moment(date).format('YYYY-MM-DD'))
    ) {
      return true;
    }

    if (!availableRange) return false;

    if (availableRange.endDate && availableRange.endDate !== 'None') {
      const isBetween = moment(date).isBetween(
        availableRange.startDate,
        availableRange.endDate,
        'day',
        '[]'
      );

      if (isBetween) {
        const dayOfDate = moment(date).format('ddd');
        return !availableRange.daysOfWeek.includes(dayOfDate);
      }
    } else {
      const isSameOrAfter = moment(date).isSameOrAfter(
        availableRange.startDate,
        'day'
      );

      if (isSameOrAfter) {
        const dayOfDate = moment(date).format('ddd');
        return !availableRange.daysOfWeek.includes(dayOfDate);
      }
    }

    return true;
  };

  return (
    <div className={classNames(classes.wrapper, className)}>
      {isIcon ? (
        <label htmlFor={id ? id : 'singleDate'}>
          <DateRangeIcon className={classes.calendarButton} />
        </label>
      ) : (
        <>
          {isInput ? (
            <label
              htmlFor={id ? id : 'singleDate'}
              className={classNames(classes.inputLabel, inputLabelClass)}
            >
              {t(label)}
            </label>
          ) : (
            <label className={classNames(classes.label, labelClass)}>
              <Typography
                variant={labelTypographyVariant ? labelTypographyVariant : 'h2'}
              >
                {t(label)}
              </Typography>
              <Typography variant="body1" className={classes.selectedDate}>
                {input.value &&
                  moment(input.value)
                    .locale(LOCALES_OBJ[selectedLanguage])
                    .format('MMM D, YYYY')}
              </Typography>
            </label>
          )}

          {isInput && (
            <label
              htmlFor={id ? id : 'singleDate'}
              className={classNames(
                classes.customInputDate,
                {
                  [classes.focus]: false
                },
                {
                  [classes.errorInput]: (touched || submitFailed) && error
                },
                customInputClass
              )}
            >
              {isWidgetSearch ? (
                <>
                  {input.value ? (
                    <div>
                      {moment(input.value)
                        .locale(LOCALES_OBJ[selectedLanguage])
                        .format('MMM D, YYYY')}
                    </div>
                  ) : (
                    <div>{placeholder ? t(placeholder) : t('Date')}</div>
                  )}
                  <CalendarImage fill={theme.baseUI.color} />
                </>
              ) : (
                <>
                  <DateRangeIcon className={classes.calendarIcon} />

                  {input.value ? (
                    <div>
                      {moment(input.value)
                        .locale(LOCALES_OBJ[selectedLanguage])
                        .format('MMM D, YYYY')}
                    </div>
                  ) : (
                    <div>{t('Date')}</div>
                  )}
                </>
              )}
            </label>
          )}
        </>
      )}

      <div
        className={classNames(classes.hiddenDateRangePicker, {
          ['inputSinglePicker']: isInput
        })}
      >
        {render && (
          <DatePicker
            verticalSpacing={0}
            readOnly={true}
            id={id ? id : 'singleDate'}
            displayFormat="MMM D, YYYY"
            date={input.value || null}
            onDateChange={date => {
              onChangeHandler(date);
            }}
            onPrevMonthClick={onPrevMonthClickHandler}
            onNextMonthClick={onNextMonthClickHandler}
            navNext={isHiddenNextButton ? <div>{''}</div> : ''}
            focused={isInput ? focus : true}
            onFocusChange={({ focused }) => {
              if (!focused && closeCalendarCallback) closeCalendarCallback();
              if (isInput) setFocus(focused);
            }}
            initialVisibleMonth={() => renderMonth}
            isDayBlocked={date => {
              const isDayBlocked = isBlockedDay(date);
              return isBlockDayByCutoffPeriod(date, isDayBlocked);
            }}
            daySize={daySize ? daySize : 38}
            numberOfMonths={numberOfMonths}
            // numberOfMonths={isSmallScreen ? 1 : 2}
            // daySize={isSmallScreen ? 30 : 38}
            renderMonthElement={({ month }) =>
              moment(month)
                .locale(LOCALES_OBJ[selectedLanguage])
                .format('MMMM YYYY')
            }
            renderDayContents={day => {
              if (selectedLanguage === 'Norway') {
                day._locale._weekdaysMin = [
                  'Sø',
                  'Ma',
                  'Ti',
                  'On',
                  'To',
                  'Fr',
                  'Lø'
                ];
              } else {
                day._locale._weekdaysMin = [
                  'Su',
                  'Mo',
                  'Tu',
                  'We',
                  'Th',
                  'Fr',
                  'Sa'
                ];
              }

              const { disableLeft, disableRight } = getIsHalf(
                disabledDates,
                day
              );

              return (
                <div
                  className={classNames({
                    [classes.dayHalfAvailable]: disableLeft || disableRight,
                    [classes.dayLeftDisable]: disableLeft,
                    [classes.dayRightDisable]: disableRight
                  })}
                >
                  <div>{day.format('D')}</div>
                </div>
              );
            }}
            hideKeyboardShortcutsPanel={true}
          />
        )}
      </div>

      {(touched || submitFailed) && error && (
        <FormHelperText
          className={classNames({ [classes.error]: isInput })}
          error
        >
          {t(error)}
        </FormHelperText>
      )}
    </div>
  );
};

export default SingleDatePicker;
